import {
  Button,
  Heading,
  HStack,
  IconButton,
  Spacer,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { TenantMemberSelector } from 'apps/TenantApp/components/MemberSelector/TenantMemberSelector';
import { MAX_AMOUNT_VAULT_MEMBERS } from 'apps/TenantApp/screens/Vaults/screens/VaultsManager/components/CreateVaultModal';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { RemoveUserIcon } from 'components/icons';
import { OwnerPanel, OwnerPanelTitle } from 'components/ownership/OwnerPanel';
import { OverlayProps, TaskOverlayBody } from 'components/TaskOverlay';
import { FirstStepTaskOverlay } from 'components/TaskOverlay/FirstStepTaskOverlay';
import { UserHandle } from 'components/UserHandle';
import React, { useState } from 'react';
import {
  graphql,
  useFragment,
  useMutation,
  useSafeRelay,
} from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import {
  VaultAccessDetails_managers_Query,
  VaultAccessDetails_managers_Query$data,
} from './__generated__/VaultAccessDetails_managers_Query.graphql';
import {
  VaultAccessDetails_node$data,
  VaultAccessDetails_node$key,
} from './__generated__/VaultAccessDetails_node.graphql';
import { VaultAccessDetails_updateVaultMembers_Mutation } from './__generated__/VaultAccessDetails_updateVaultMembers_Mutation.graphql';
import {
  VaultManagementGroupOption,
  VaultManagementGroupSelect,
} from 'apps/TenantApp/screens/Vaults/components/VaultManagementGroupSelect';
import { useFeatureFlag } from 'utils/sessionProvider';
import { VaultManagementGroupLabel } from 'apps/TenantApp/screens/Vaults/components/VaultManagementGroupLabel';
import { VaultAccessDetails_assignVaultManagementGroup_Mutation } from './__generated__/VaultAccessDetails_assignVaultManagementGroup_Mutation.graphql';

const query = graphql`
  query VaultAccessDetails_managers_Query($skip: Boolean!) {
    vaultManager @skip(if: $skip) {
      managers {
        edges {
          node {
            id
            ...UserHandle_member
          }
        }
      }
      ...VaultManagementGroupSelect_vaultManager
    }
  }
`;

const updateMembersMutation = graphql`
  mutation VaultAccessDetails_updateVaultMembers_Mutation(
    $vaultId: ID!
    $memberIds: [ID!]!
  ) {
    updateVaultMembers(input: { vaultId: $vaultId, memberIds: $memberIds }) {
      vaultUpdated {
        id
        ...VaultAccessDetails_node
      }
    }
  }
`;

const fragment = graphql`
  fragment VaultAccessDetails_node on Vault {
    id
    canUpdate {
      value
    }
    name
    members {
      id
      name
      ...UserHandle_member
    }
    vaultManagementGroup {
      id
      name
      ...VaultManagementGroupLabel_vaultManagementGroup
    }
  }
`;

interface VaultAccessDetailsProps {
  node: VaultAccessDetails_node$key;
}

export function VaultAccessDetails({ node }: VaultAccessDetailsProps) {
  const isVaultManagementGroupsEnabled = useFeatureFlag(
    'FEATURE_VAULT_MANAGEMENT_GROUPS',
  );

  const { useLazyLoadQuery } = useSafeRelay();
  const {
    id,
    members,
    canUpdate: { value: canUpdate },
    vaultManagementGroup,
  } = useFragment(fragment, node);
  const { vaultManager } = useLazyLoadQuery<VaultAccessDetails_managers_Query>(
    query,
    {
      skip: !canUpdate,
    },
  );
  const vaultManagers =
    vaultManager?.managers.edges?.map((m) => m.node).filter(notEmpty) ?? [];

  const [updateMembers, isUpdateMembersLoading] =
    useMutation<VaultAccessDetails_updateVaultMembers_Mutation>(
      updateMembersMutation,
    );
  const isSingleMember = members.length === 1;

  const currentMemberIds = members.map(({ id }) => id);
  const onRemoveMember = (memberId: string, onCompleted: () => void) => {
    updateMembers({
      variables: {
        vaultId: id,
        memberIds: currentMemberIds.filter(
          (currentMemberId) => currentMemberId !== memberId,
        ),
      },
      onCompleted,
    });
  };
  const onAddMemers = (newMemberIds: string[], onCompleted: () => void) => {
    updateMembers({
      variables: {
        vaultId: id,
        memberIds: newMemberIds,
      },
      onCompleted,
    });
  };
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <Stack spacing={4}>
      <OwnerPanel
        title={
          <OwnerPanelTitle
            onAddOwners={canUpdate ? onOpen : undefined}
          >{t`Vault members`}</OwnerPanelTitle>
        }
      >
        {members.map((member) => {
          return (
            <HStack key={member.id} data-qa="vault-member">
              <UserHandle
                showAvatar
                avatarProps={
                  isVaultManagementGroupsEnabled
                    ? {
                        size: 'xs',
                      }
                    : {}
                }
                key={member.id}
                member={member}
                linkToProfile
              />
              <Spacer />

              {!isSingleMember && canUpdate && (
                <RemoveVaultMemberButton
                  isLoading={isUpdateMembersLoading}
                  member={member}
                  onRemoveMember={onRemoveMember}
                />
              )}
            </HStack>
          );
        })}
      </OwnerPanel>

      {isVaultManagementGroupsEnabled ? (
        <VaultManagementPanel
          vaultId={id}
          vaultManager={vaultManager}
          vaultManagementGroup={vaultManagementGroup}
        />
      ) : (
        !!vaultManagers.length && (
          <OwnerPanel
            title={<OwnerPanelTitle>{t`Vault managers`}</OwnerPanelTitle>}
          >
            {vaultManagers.map((manager) => {
              return (
                <HStack key={manager.id} data-qa="vault-manager">
                  <UserHandle
                    showAvatar
                    key={manager.id}
                    member={manager}
                    linkToProfile
                  />
                  <Spacer />
                </HStack>
              );
            })}
          </OwnerPanel>
        )
      )}

      <AddMemberModal
        isOpen={isOpen}
        onClose={onClose}
        currentMemberIds={currentMemberIds}
        onAddMembers={onAddMemers}
        isAddMembersLoading={isUpdateMembersLoading}
      />
    </Stack>
  );
}

const assignVaultManagementGroupMutation = graphql`
  mutation VaultAccessDetails_assignVaultManagementGroup_Mutation(
    $vaultId: ID!
    $vaultManagementGroupId: ID!
  ) {
    assignVaultManagementGroup(
      input: {
        vaultId: $vaultId
        vaultManagementGroupId: $vaultManagementGroupId
      }
    ) {
      vaultUpdated {
        id
        ...VaultAccessDetails_node
      }
    }
  }
`;

interface VaultManagementPanelProps {
  vaultId: string;
  vaultManager: VaultAccessDetails_managers_Query$data['vaultManager'];
  vaultManagementGroup: VaultAccessDetails_node$data['vaultManagementGroup'];
}

const VaultManagementPanel = ({
  vaultId,
  vaultManager,
  vaultManagementGroup,
}: VaultManagementPanelProps) => {
  const bg = useColorModeValue('gray.50', 'gray.700');

  const [isEditing, setIsEditing] = useState(false);

  const [
    selectedVaultManagementGroupOption,
    setSelectedVaultManagementGroupOption,
  ] = useState<VaultManagementGroupOption | null>(null);

  const [assignVaultManagementGroup, isAssignVaultManagementGroupLoading] =
    useMutation<VaultAccessDetails_assignVaultManagementGroup_Mutation>(
      assignVaultManagementGroupMutation,
    );

  if (!vaultManager) {
    return null;
  }

  return (
    <Stack>
      <HStack>
        <Heading size="sm">{t`Vault Management`}</Heading>
        <Spacer />
        <Button
          variant="ghost"
          colorScheme="blue"
          size="sm"
          visibility={isEditing ? 'hidden' : 'visible'}
          onClick={() => {
            setIsEditing(true);
          }}
        >{t`Edit`}</Button>
      </HStack>

      <Stack bg={bg} borderRadius="md" p={2}>
        {isEditing ? (
          <>
            <VaultManagementGroupSelect
              defaultVaultManagementGroupOption={{
                label: (
                  <VaultManagementGroupLabel
                    vaultManagementGroup={vaultManagementGroup}
                    pl={1}
                  />
                ),
                id: vaultManagementGroup.id,
                name: vaultManagementGroup.name,
              }}
              vaultManager={vaultManager}
              onSelect={setSelectedVaultManagementGroupOption}
            />

            <HStack>
              <Spacer />
              <Button
                variant="solid"
                colorScheme="gray"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                }}
              >{t`Cancel`}</Button>
              <Button
                variant="solid"
                colorScheme="blue"
                size="sm"
                isLoading={isAssignVaultManagementGroupLoading}
                isDisabled={
                  !selectedVaultManagementGroupOption ||
                  selectedVaultManagementGroupOption.id ===
                    vaultManagementGroup.id
                }
                onClick={() => {
                  if (!selectedVaultManagementGroupOption) return;
                  assignVaultManagementGroup({
                    variables: {
                      vaultId,
                      vaultManagementGroupId:
                        selectedVaultManagementGroupOption.id,
                    },
                    onCompleted: () => {
                      setIsEditing(false);
                    },
                  });
                }}
              >{t`Save`}</Button>
            </HStack>
          </>
        ) : (
          <VaultManagementGroupLabel
            vaultManagementGroup={vaultManagementGroup}
            fontWeight="bold"
          />
        )}
      </Stack>
    </Stack>
  );
};

interface RemoveVaultMemberButtonProps {
  isLoading: boolean;
  member: {
    name: string;
    id: string;
  };

  onRemoveMember(memberId: string, closeConfirmation: () => void): void;
}

const RemoveVaultMemberButton = ({
  isLoading,
  member,
  onRemoveMember,
}: RemoveVaultMemberButtonProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <>
      <IconButton
        aria-label={t`Remove vault member`}
        isLoading={isLoading}
        variant="ghost"
        size="sm"
        icon={<RemoveUserIcon />}
        onClick={onOpen}
      />
      <ConfirmationDialog
        action={{
          name: t`Remove vault member`,
          onAction: () => {
            onRemoveMember(member.id, onClose);
          },
          inProgress: isLoading,
        }}
        isOpen={isOpen}
        onClose={onClose}
        confirmationText={t`Remove vault member`}
      >
        {t`Are you sure you want to remove ${member.name} as member of this vault?`}
      </ConfirmationDialog>
    </>
  );
};

interface AddMemberModalProps extends OverlayProps {
  currentMemberIds: string[];

  onAddMembers(newMemberIds: string[], onCompleted: () => void): void;

  isAddMembersLoading: boolean;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  currentMemberIds,
  onAddMembers,
  isOpen,
  onClose,
  isAddMembersLoading,
}) => {
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

  const hasTooManyMembers = selectedMemberIds.length > MAX_AMOUNT_VAULT_MEMBERS;
  const hasTooLittleMembers = selectedMemberIds.length < 1;

  return (
    <FirstStepTaskOverlay
      title={t`Add vault members  `}
      submitButtonTitle={t`Done`}
      isOpen={isOpen}
      onClose={onClose}
      isDisabled={hasTooManyMembers || hasTooLittleMembers}
      onCompleteTask={() => {
        onAddMembers(selectedMemberIds, () => {
          setSelectedMemberIds([]);
          onClose();
        });
      }}
      isLoading={isAddMembersLoading}
    >
      <TaskOverlayBody>
        <Stack py={4}>
          {hasTooManyMembers && (
            <Text color="red.500">{t`Vaults have a maximum of ${MAX_AMOUNT_VAULT_MEMBERS} members.`}</Text>
          )}
          {hasTooLittleMembers && (
            <Text color="red.500">{t`A vault should have at least 1 member.`}</Text>
          )}
          <TenantMemberSelector
            selectedMemberIds={currentMemberIds}
            disableEmailInvite
            selectedEmails={[]}
            onSelectedMembersUpdate={setSelectedMemberIds}
            onSelectedEmailsUpdate={() => {}}
            allowSelectAll={false}
          />
        </Stack>
      </TaskOverlayBody>
    </FirstStepTaskOverlay>
  );
};
